import React, { useState, useEffect } from "react";
import { Navigate, Link, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { getToken } from "../../api/auth";
import { getSalesHeaderByNo } from "../../api/order";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./OrderDetails.css";

const OrderDetails = () => {
  const { id } = useParams();
  const token = getToken();
  const [order, setOrder] = useState(null);
  const [salesLines, setSalesLines] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      setLoading(true);
      setError(null); // Clear previous errors
      try {
        const response = await getSalesHeaderByNo(id);

        // Ensure response exists
        if (!response) {
          setError("Invalid response format");
          setOrder(null);
          setSalesLines([]);
          return;
        }

        // Check if response is wrapped (e.g., { order: ... } or { data: ... })
        let selectedOrder = response;
        if (response.order) {
          selectedOrder = response.order;
        } else if (response.data) {
          selectedOrder = response.data;
        } else if (Array.isArray(response) && response.length > 0) {
          selectedOrder = response[0];
        }

        if (selectedOrder && selectedOrder.no) {
          setOrder({
            documenttype: selectedOrder.documentType,
            no: selectedOrder.no,
            selltocustomerno: selectedOrder.sellToCustomerNo,
            selltocustomername: selectedOrder.sellToCustomerName,
            selltoaddress: selectedOrder.sellToAddress,
            selltocity: selectedOrder.sellToCity,
            selltocontact: selectedOrder.sellToContact,
            orderdate: selectedOrder.orderDate,
            documentdate: selectedOrder.documentDate,
            salespersoncode: selectedOrder.salesPersonCode,
            status: selectedOrder.status,
            controllerName: selectedOrder.controllerName,
            assigneduserid: selectedOrder.assignedUserId,
            headeramount: selectedOrder.headerAmount,
            paymentmethodcode: selectedOrder.paymentMethodCode,
            companycode: selectedOrder.companyCode,
            startTime: selectedOrder.startTime,
            endTime: selectedOrder.endTime,
          });

          setSalesLines(
            (selectedOrder.saleslines || []).map((line) => ({
              documenttype: line.documenttype,
              documentno: line.documentno,
              lineno: line.lineno,
              selltocustomerno: line.selltocustomerno,
              no: line.no,
              locationcode: line.locationcode,
              shipmentdate: line.shipmentdate,
              description: line.description,
              unitofmeasure: line.unitofmeasure,
              quantity: line.quantity,
              originalQuantity: line.originalQuantity,
              unitprice: line.unitprice,
              vat: line.vat,
              linediscount: line.linediscount || 0,
              linediscountamount: line.linediscountamount || 0,
              amount: line.amount,
              amountincludingvat: line.amountincludingvat,
              lineamount: line.lineamount || 0,
              unitofmeasurecode: line.unitofmeasurecode,
              barkodi: line.barkodi,
              amobilestatus: line.amobilestatus,
              companycode: line.companycode,
            }))
          );
        } else {
          setOrder(null);
          setSalesLines([]);
          setError("Order not found");
        }
      } catch (err) {
        console.error("Failed to fetch order details:", err);
        setError("Failed to fetch order details. Please try again.");
        setOrder(null);
        setSalesLines([]);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchOrderDetails();
    } else {
      console.log("No token found, redirecting to login"); // Debug
    }
  }, [id, token]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedSalesLines = [...salesLines]
    .filter((line) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        (line.no && line.no.toLowerCase().includes(query)) ||
        (line.description && line.description.toLowerCase().includes(query)) ||
        (line.barkodi && line.barkodi.toLowerCase().includes(query)) ||
        (line.unitofmeasurecode &&
          line.unitofmeasurecode.toLowerCase().includes(query)) ||
        (line.lineno && line.lineno.toString().includes(query))
      );
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      const valA = a[sortConfig.key];
      const valB = b[sortConfig.key];

      if (typeof valA === "string") {
        return sortConfig.direction === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

  const getStatusName = (status) => {
    const statuses = {
      0: "Inserted",
      1: "Assigned to Agent",
      2: "Assigned to Controller",
      3: "Completed",
    };
    return statuses[status] || "Unknown";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0:
        return "#f59e0b";
      case 1:
        return "#3b82f6";
      case 2:
        return "#8910b9ff";
      case 3:
        return "#22c55e";
      default:
        return "#ffffff";
    }
  };

  const getMobileStatusName = (amobilestatus) => {
    const statuses = {
      0: "Inserted",
      1: "Agent Completed",
      2: "Mistake",
      3: "Approved",
    };
    return statuses[amobilestatus] || "Unknown";
  };

  const getMobileStatusColor = (amobilestatus) => {
    switch (amobilestatus) {
      case 0:
        return "#f59e0b";
      case 1:
        return "#3b82f6";
      case 2:
        return "#b91b10ff";
      case 3:
        return "#22c55e";
      default:
        return "#ffffff";
    }
  };

  // Export to Excel Function
  const exportToExcel = () => {
    if (!order || salesLines.length === 0) {
      alert("No data to export.");
      return;
    }

    // Prepare header data as array of arrays
    const headerData = [
      ["Order No", order.no],
      ["Customer No", order.selltocustomerno],
      ["Customer Name", order.selltocustomername || "N/A"],
      ["Address", order.selltoaddress || "N/A"],
      ["City", order.selltocity || "N/A"],
      ["Contact", order.selltocontact || "N/A"],
      [
        "Order Date",
        order.orderdate
          ? new Date(order.orderdate).toLocaleDateString()
          : "N/A",
      ],
      [
        "Document Date",
        order.documentdate
          ? new Date(order.documentdate).toLocaleDateString()
          : "N/A",
      ],
      ["Salesperson Code", order.salespersoncode || "N/A"],
      ["Status", getStatusName(order.status)],
      ["Controller Name", order.controllerName || "N/A"],
      ["Assigned User ID", order.assigneduserid || "N/A"],
      ["Header Amount", `€${Number(order.headeramount).toFixed(2)}`],
      ["Payment Method Code", order.paymentmethodcode || "N/A"],
      ["Company Code", order.companycode || "N/A"],
      ["Start Time", order.startTime || "N/A"],
      ["End Time", order.endTime || "N/A"],
      [], // Blank row
    ];

    // Sales lines header
    const linesHeader = [
      "Line No",
      "Product No",
      "Description",
      "Unit of Measure Code",
      "Quantity",
      "Original Quantity",
      "Barcode",
      "Mobile Status",
      "Unit Price", // Add more if needed
    ];

    // Sales lines data
    const linesData = sortedSalesLines.map((line) => [
      line.lineno,
      line.no,
      line.description || "N/A",
      line.unitofmeasurecode || "N/A",
      Number(line.quantity).toFixed(2),
      Number(line.originalQuantity).toFixed(2),
      line.barkodi || "N/A",
      getMobileStatusName(line.amobilestatus),
      `€${Number(line.unitprice).toFixed(2)}`,
    ]);

    // Combine all into aoa (array of arrays)
    const aoa = [...headerData, linesHeader, ...linesData];

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Auto-size columns (approximate)
    const colWidths = aoa.reduce((acc, row) => {
      row.forEach((cell, idx) => {
        const len = cell ? cell.toString().length : 0;
        acc[idx] = Math.max(acc[idx] || 10, len + 2);
      });
      return acc;
    }, []);
    ws["!cols"] = colWidths.map((w) => ({ wch: w }));

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Order Details");

    // Generate and download
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Order_${order.no}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading order details...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-details-container">
        <div className="empty-state">
          <p>Order not found</p>
          {error && <p style={{ color: "red", marginTop: "10px" }}>{error}</p>}
          <Link to="/orders" className="glass-btn btn-primary">
            <FaArrowLeft size={16} /> Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="order-details-container">
      <div className="page-header">
        <h2 className="order-details-title">Order Details: {order.no}</h2>

        <div className="header-actions">
          <Link to="/orders" className="glass-btn btn-primary back-btn compact">
            <FaArrowLeft></FaArrowLeft> Back to Orders
          </Link>

          <button
            className="glass-btn export-btn compact"
            onClick={exportToExcel}
            disabled={!order || salesLines.length === 0}
          >
            Export To Excel
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="order-header-cards-container">
        {/* Customer Info Card */}
        <div className="order-header-card">
          <h3 className="card-title">Customer Information</h3>
          <div className="order-header-field">
            <label>Customer:</label>
            <span>{order.selltocustomername || "N/A"}</span>
          </div>
          <div className="order-header-field">
            <label>Customer No:</label>
            <span>{order.selltocustomerno}</span>
          </div>
          <div className="order-header-field">
            <label>Address:</label>
            <span>{order.selltoaddress || "N/A"}</span>
          </div>
          <div className="order-header-field">
            <label>City:</label>
            <span>{order.selltocity || "N/A"}</span>
          </div>
          <div className="order-header-field">
            <label>Contact:</label>
            <span>{order.selltocontact || "N/A"}</span>
          </div>
        </div>

        {/* Order Info Card */}
        <div className="order-header-card">
          <h3 className="card-title">Order Information</h3>
          <div className="order-header-field">
            <label>Order Date:</label>
            <span>{new Date(order.orderdate).toLocaleDateString()}</span>
          </div>
          <div className="order-header-field">
            <label>Document Date:</label>
            <span>{new Date(order.documentdate).toLocaleDateString()}</span>
          </div>
          <div className="order-header-field">
            <label>Status:</label>
            <span
              style={{
                backgroundColor: getStatusColor(order.status),
                padding: "2px 8px",
                borderRadius: "4px",
                color: "#fff",
              }}
            >
              {getStatusName(order.status)}
            </span>
          </div>
          <div className="order-header-field">
            <label>Total Amount:</label>
            <span>€{Number(order.headeramount).toFixed(2)}</span>
          </div>
          <div className="order-header-field">
            <label>Payment Method:</label>
            <span>{order.paymentmethodcode || "N/A"}</span>
          </div>
        </div>

        {/* Additional Details Card */}
        <div className="order-header-card">
          <h3 className="card-title">Additional Details</h3>
          <div className="order-header-field">
            <label>Controller:</label>
            <span>{order.controllerName || "N/A"}</span>
          </div>
          <div className="order-header-field">
            <label>Assigned User:</label>
            <span>{order.assigneduserid || "N/A"}</span>
          </div>
          <div className="order-header-field">
            <label>Company:</label>
            <span>{order.companycode || "N/A"}</span>
          </div>
          <div className="order-header-field">
            <label>Start Time:</label>
            <span>{order.startTime || "N/A"}</span>
          </div>
          <div className="order-header-field">
            <label>End Time:</label>
            <span>{order.endTime || "N/A"}</span>
          </div>
        </div>
      </div>

      {/* Sales Lines */}
      <div className="order-lines-container">
        <div className="sales-lines-header">
          <h3>Sales Lines</h3>
          <input
            type="text"
            placeholder="Search by product, description, barcode..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        {sortedSalesLines.length > 0 ? (
          <table className="order-lines-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("lineno")}>Line No</th>
                <th onClick={() => handleSort("no")}>Product No</th>
                <th onClick={() => handleSort("description")}>Description</th>
                <th onClick={() => handleSort("unitofmeasurecode")}>Unit</th>
                <th onClick={() => handleSort("quantity")}>Quantity</th>
                <th onClick={() => handleSort("originalQuantity")}>
                  Original Quantity
                </th>
                <th onClick={() => handleSort("barkodi")}>Barcode</th>
                <th onClick={() => handleSort("amobilestatus")}>
                  Mobile Status
                </th>
                <th onClick={() => handleSort("unitprice")}>Unit Price</th>
                {/* <th onClick={() => handleSort("lineamount")}>Line Amount</th> */}
              </tr>
            </thead>
            <tbody>
              {sortedSalesLines.map((line, idx) => (
                <tr
                  key={idx}
                  className={`row-animate row-animate-${Math.min(idx, 3)} ${
                    Number(line.quantity) !== Number(line.originalQuantity)
                      ? "highlight-row"
                      : ""
                  }`}
                >
                  <td>{line.lineno}</td>
                  <td>{line.no}</td>
                  <td>{line.description || "N/A"}</td>
                  <td>{line.unitofmeasurecode || "N/A"}</td>
                  <td>{Number(line.quantity).toFixed(2)}</td>
                  <td>{Number(line.originalQuantity).toFixed(2)}</td>
                  <td>{line.barkodi || "N/A"}</td>
                  <td>
                    <span
                      style={{
                        backgroundColor: getMobileStatusColor(
                          line.amobilestatus
                        ),
                        padding: "2px 8px",
                        borderRadius: "4px",
                        color: "#fff",
                      }}
                    >
                      {getMobileStatusName(line.amobilestatus)}
                    </span>
                  </td>
                  <td>€{Number(line.unitprice).toFixed(2)}</td>
                  {/* <td>€{Number(line.lineamount).toFixed(2)}</td> */}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <p>No sales lines found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetails;
